/** /zh/prd-template — Chinese version of the PRD-template landing page.
 *  Native zh content, same structure as PrdTemplate.tsx. */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

export function PrdTemplateZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/prd-template"
      documentTitle="不会漂移的 PRD 模板 · Draftlize"
      documentDescription="一份能直接抄走的 PRD 模板——问题、目标、非目标、目标用户、用户故事、功能需求、成功指标、依赖、待解决问题。可问题在于：写在 Notion 或 Google Doc 里的 PRD 模板，填完那一刻就开始过时。在 Draftlize 里，每一节都是结构化卡片，上游决策一改，依赖它的卡片自动标记 stale。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">模板 · PRD</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            一份不会漂移的<br/>
            <em>PRD 模板。</em>
          </h1>
          <p className="lede">
            你为一份 <em>PRD 模板</em>而来——各个章节、标题、一份能粘进文档、用来起一份<a href="/zh/product-requirements-document" style={{ color: 'var(--amber)' }}>产品需求文档（PRD）</a>的提纲。它就在下面，拿走，是份真东西。但你要清楚拿到的是什么：模板是一份填空式提纲，而你填完的那一刻，它就开始漂移。一个 Notion 页面或一份 Google Doc 根本不知道，你写下的某个目标，什么时候已经和它底下的决策对不上了——于是 PRD 悄悄偏离团队真正在做的东西。Draftlize 给你同样的九个章节——并把每一节都变成 AI agent 能读也能写的卡片：一个决策一动，所有下游就把自己标记成 stale。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">模板正文</span>
            <h2 className="serif">九个章节。<em>随便抄到哪。</em></h2>
          </div>
          <p>这就是完整的 PRD 格式。文档、wiki、markdown 文件里都能用——粘进去填就行。直接用。下一节讲的是：当每个标题不再是一段文字、而成为一张卡片时，会发生什么变化。</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: '1 · 问题陈述',
              hint: '必填',
              body: '一两句话讲清用户的痛——什么疼、对谁疼、为什么现在重要。如果你不提自己的方案就说不清这个问题，那你还没有问题陈述，你只有一个在找理由的功能。',
            },
            {
              k: '2 · 目标',
              hint: '结果',
              body: '把「成功」写成结果，而不是功能。是「把首稿耗时压到五分钟以内」，不是「加一个模板按钮」。最多三个——写了九个目标的 PRD，等于一个目标都没有。',
            },
            {
              k: '3 · 非目标',
              hint: '不做什么',
              body: '这一版你刻意不做的事，写下来，免得第三周又被翻出来重新吵。任何 PRD 模板里最被低估的一节，也是最能省下争论的一节。',
            },
            {
              k: '4 · 目标用户',
              hint: '为谁',
              body: '这东西具体为谁做——角色、场景、他想完成什么。是「在做副业项目、想尽快上线的独立开发者」，不是「用户」。往下的一切，从故事到指标，都锚在这一节上。',
            },
            {
              k: '5 · 用户故事',
              hint: '要做的事',
              body: '作为 [某用户]，我想要 [某能力]，以便 [某结果]。从问题通向需求的桥——每个故事都是产品必须完成的一件事，用用户的话说，而不是系统的话。',
            },
            {
              k: '6 · 功能需求',
              hint: '必须做到什么',
              body: '产品必须支持的具体行为，编上号，方便引用。这是最长的一节，也是工程真正照着建的那一节。每条需求都应往回追到一个故事，往前连到一个指标。',
            },
            {
              k: '7 · 成功指标',
              hint: '怎么算成',
              body: '能告诉你「目标达成了」的那些数——激活、留存、那个真正在动的漏斗环节。上线前就定下来，否则上线后你会把发生的一切，事后都圆成一场胜利。',
            },
            {
              k: '8 · 依赖',
              hint: '它依赖什么',
              body: '这份 PRD 假设为真的那些决策、系统和别处的工作——定价、鉴权模型、某个上游 API。在文档里，这是一份你会忘记更新的清单。它是决定 PRD 是否还诚实的那一节。',
            },
            {
              k: '9 · 待解决问题',
              hint: '尚未确定',
              body: '你还不知道的东西，明明白白写出来，而不是藏起来。一份假装样样都有答案的 PRD 是虚构；待解决问题这份清单，才是接下来两周真正的活儿所在。',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          模板到此为止。下面讲：为什么「填完」正是它开始漂移的那一刻——以及我们怎么解决。
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">为什么 PRD 模板会<em>漂移。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">模板是一份提纲，不是一个系统</h3>
            <p>这九个章节是好的——几乎每一种 PRD 格式最后都收敛到这个形状。但提纲只把产品记录一次。它不知道某个目标什么时候和它底下的决策对不上了，因为一页文字没法盯着项目其它部分在它周围变化。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">「依赖」那一节是个你会失信的承诺</h3>
            <p>你列出 PRD 依赖什么——定价、鉴权模型、上个 sprint 定下的某个决策。然后其中一个变了，对齐文档就成了手动考古：把每条需求重读一遍，找出那些假设了旧结论的。没人会做，于是本该让 PRD 保持诚实的那一节，第一个过时。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">「填完」就是最高水位线</h3>
            <p>PRD 在你写完后的那一分钟最准，此后再也没那么准过。等工程做到一半，它里头有相当一部分已经是错的，而你说不清是哪一部分——于是团队不再信这份文档，开始在 Slack 里问，而这正是 PRD 本该防住的失败。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">同一份模板，<em>变成活的。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">每一节成为一张卡片，而不是一个标题</h3>
            <p>在 Draftlize 里，PRD 不是一份空白文档——而是沿着那几个章节排开的一组结构化卡片：问题、目标、非目标、用户、故事、需求、指标、依赖、待解决问题。结构化、可按 ID 寻址、能从一份 spec 或一个 thread 引用，而不是埋在往下滚三屏的某个标题底下。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">改一个决策，依赖自动标 stale</h3>
            <p>因为「依赖」是一条真实的连接，而不是一串名词，改一个决策会让每张依赖它的卡片自动变 stale——就像构建系统让一个改动文件的所有下游失效一样。改掉定价决策，假设了它的需求和指标会亮起来，而不是悄悄撒谎。模板许下的承诺，由 substrate 替你守住。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI agent 能读它，也能写它</h3>
            <p>Claude Code 或 Cursor，经 MCP，在起草下一条需求前读完每张相关卡片，并把新章节按同样的形状写回去。PRD 不再是一份给「只草草扫一眼的读者」的文档，而成了在每一轮都被读到的上下文——和一份给底层结论用的<a href="/zh/decision-log-template" style={{ color: 'var(--amber)' }}>决策日志模板</a>天然搭配。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          模板告诉你该写哪些章节。它没法告诉你，某一节什么时候已经和它底下的决策对不上了。
        </blockquote>
        <cite>留着这九个章节。让 substrate 替你把它们保持最新。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '一份 PRD 模板该包含哪些部分？', a: '至少要有：问题陈述、目标、非目标、目标用户、用户故事、功能需求、成功指标、依赖、待解问题。这九个部分是大多数 PRD 格式收敛出来的形状——用不上的标题可以跳过，但底下那个问题要答。' },
            { q: 'PRD 模板和 PRD 范例有什么区别？', a: '模板是空白提纲——你要填的那些章节标题。范例是已经针对某个具体产品填好的模板，让你看到该写到多细。用模板来写，看范例来对齐颗粒度。' },
            { q: '可以用 ChatGPT 或 AI 来填 PRD 模板吗？', a: '可以——AI 写作器能把一份粗略的 brief 很快变成一版像样的初稿。缺口在于持久性：它只起草一次，之后它假设的某个决策变了，它并不知道。这正是模板加 AI 写作器都解决不了的部分，也是各章节会漂移的原因。' },
            { q: 'Draftlize 的 PRD 模板和 Notion / Google Docs 里的有什么不同？', a: 'Notion 或 Docs 里的模板是一份静态提纲——你填完的那一刻它就开始过期。Draftlize 给你同样的九个章节，但每个是一张有类型的卡片：可按 ID 寻址，AI agent 经 MCP 读写，某个决策一变，每个依赖它的章节自动标记为过期，而不是继续静静地撒谎。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">用 $5 <em>免费开始你的 PRD。</em></h2>
        </div>
        <div className="trust-strip">
          <span>注册送 $5</span>
          <span>按量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            把上面的模板拿走，或者建一个项目，让 agent 从一份粗略的 brief 起草出产品需求文档（PRD）——然后替你把每一节、以及它依赖的每一个决策，都保持最新。
          </p>
          <a className="btn-primary" href="/">用 $5 免费开始</a>
        </div>
      </section>
      <TemplateCta template="prd" locale="zh" />
    </MarketingShell>
  );
}
